/**
 * "What we monitor", each member's "Understanding" and "Members to learn
 * from", straight from pv-auth (src/services/admin/monitoringAdmin.service.ts):
 *   GET /provider/admin/monitoring                  the catalog, live flags, today's counts
 *   GET /provider/admin/people/:id/understanding    one member's rows and the plain summary
 *   GET /provider/admin/understanding/members       members ranked by how much we hold
 * Every read is SUPER_ADMIN only and lands in AdminAccessLog before any row
 * is read. Shapes mirror the service.
 *
 * Pure helpers below the fetchers. The rule that matters: a value nobody
 * measured is the words "not measured", never 0 or a blank, and an empty
 * list of told states is "not told", never "none".
 */
import { NOT_MEASURED } from '@/lib/format';
import type { TraceSummary } from '@/lib/session-data';

/* ── shapes ─────────────────────────────────────────────────────────────── */

export interface MonitoringTable {
  model: string;
  fields: string[];
  writtenAt: string;
  whereLabel: string | null;
  built: boolean;
  expectedIn: string | null;
  rowsYesterday: number | null;
  total: number | null;
  countNote: string | null;
}

export interface MonitoringFlag { name: string; live: boolean; defaultOn: boolean; gates: string; expectedIn: string | null }

export interface MonitoringSignal {
  key: string;
  name: string;
  source: string;
  sourceLabel: string;
  tables: MonitoringTable[];
  cadence: string;
  retention: string;
  readers: string[];
  readerLabels: string[];
  readersNote: string | null;
  flags: MonitoringFlag[];
  privacy: string;
}

export interface MonitoringOverview { generatedAt: string; yesterday: { from: string; to: string }; rules: string[]; signals: MonitoringSignal[] }

export type KnowledgeGroupKey = 'day_states' | 'after_check_answers' | 'about_you' | 'pain_free_movements' | 'meeting_watch_consent';
export interface KnowledgeRow { kind: string; valueText: string | null; valueInt: number | null; source: string | null; askedQuestion: string | null; at: string }
export interface KnowledgeGroup { key: KnowledgeGroupKey; label: string; built: boolean; rows: KnowledgeRow[] }

export interface UnderstandingTimezone {
  resolved: string;
  stored: string | null;
  storedAt: string | null;
  source: string;
  fromRows: string | null;
  fromRowsAt: string | null;
}

export interface UnderstandingActivation {
  id: string;
  recordedAt: string;
  exercise: string;
  exerciseName: string;
  completed: boolean;
  completedReps: number;
  targetReps: number;
  unit: string | null;
  holdSeconds: number | null;
  durationS: number;
  score: number | null;
  formScored: boolean | null;
  stabilityScore: number | null;
  trackableFramesRatio: number | null;
  felt: string | null;
}

export interface UnderstandingActiveSession {
  sessionId: string;
  platform: string;
  localDate: string;
  startedAt: string;
  endedAt: string | null;
  endReason: string | null;
  monitoredMinutes: number | null;
  avgDriftPct: number | null;
  cueCount: number | null;
  cuesCorrected: number | null;
  cameraBand: string | null;
  trackableFramesRatio: number | null;
  meetingWindows: { startAt: string; endAt: string }[];
}


export interface UnderstandingCounts {
  checks: number | null;
  checkDays: number | null;
  activeSessions: number | null;
  activeMinutes: number | null;
  activations: number | null;
  feltAnswers: number | null;
  feltOf: number;
  toldDays: number | null;
  habitDays: number;
  habitDaysMeasured: number;
  findings: number;
  shownFindings: number;
  calendarConnected: boolean | null;
  meetingWindows: number | null;
  traces: number;
  knowledgeRows: number;
}

export interface MemberUnderstanding {
  generatedAt: string;
  person: { userId: string; email: string; name: string; signupAt: string | null };
  summary: string;
  timezone: UnderstandingTimezone;
  counts: UnderstandingCounts;
  habitDays: Record<string, unknown>[];
  habitFindings: Record<string, unknown>[];
  planPick: { built: boolean; localDate: string; today: Record<string, unknown> | null; latest: Record<string, unknown> | null };
  knowledge: KnowledgeGroup[];
  knowledgeCapped: boolean;
  activations: UnderstandingActivation[];
  activeSessions: UnderstandingActiveSession[];
  /** The autopsy page's summaries (session-data TraceSummary), newest first. */
  traces: TraceSummary[];
  unavailable: string[];
}

export interface LearnFromRow { userId: string; email: string; name: string; checks: number; activeSessions: number; activations: number; toldDays: number; findings: number; breadth: number; total: number }
export interface LearnFromPage { generatedAt: string; limit: number; considered: number; rows: LearnFromRow[]; unavailable: string[]; ranking: string }

/* ── fetchers ───────────────────────────────────────────────────────────── */

export async function fetchMonitoring(): Promise<MonitoringOverview> {
  const { apiGet } = await import('@/services/base');
  return (await apiGet('/provider/admin/monitoring', true)) as MonitoringOverview;
}

export async function fetchUnderstanding(userId: string): Promise<MemberUnderstanding> {
  const { apiGet } = await import('@/services/base');
  return (await apiGet(`/provider/admin/people/${encodeURIComponent(userId)}/understanding`, true)) as MemberUnderstanding;
}

export async function fetchLearnFrom(limit = 50): Promise<LearnFromPage> {
  const { apiGet } = await import('@/services/base');
  return (await apiGet(`/provider/admin/understanding/members?limit=${encodeURIComponent(String(limit))}`, true)) as LearnFromPage;
}

/* ── words ──────────────────────────────────────────────────────────────── */

/** One stored value as words: null is "not measured", a boolean is yes or no, a number keeps at most two decimals. */
export const cellValue = (v: unknown): string => {
  if (v === null || v === undefined) return NOT_MEASURED;
  if (typeof v === 'boolean') return v ? 'yes' : 'no';
  if (typeof v === 'number') return Number.isFinite(v) ? (Number.isInteger(v) ? v.toLocaleString() : (Math.round(v * 100) / 100).toLocaleString()) : NOT_MEASURED;
  if (typeof v === 'string') return v;
  if (Array.isArray(v)) return v.length ? v.map((x) => (typeof x === 'object' && x !== null ? JSON.stringify(x) : String(x))).join(', ') : 'none';
  return JSON.stringify(v);
};

/** The HabitDay columns the Understanding table shows, in the roll-up's order, with a short heading each. */
export const HABIT_DAY_COLUMNS: { key: string; label: string }[] = [
  { key: 'localDate', label: 'Day' },
  { key: 'checkDone', label: 'Check' },
  { key: 'checkLocalMinutes', label: 'Check time' },
  { key: 'checkComposite', label: 'Composite' },
  { key: 'shoulderLowSide', label: 'Low shoulder' },
  { key: 'checkFelt', label: 'Felt' },
  { key: 'activeSessions', label: 'AM sessions' },
  { key: 'monitoredMinutes', label: 'AM minutes' },
  { key: 'cueCount', label: 'Cues' },
  { key: 'cuesCorrected', label: 'Corrected' },
  { key: 'dominantCueKind', label: 'Main cue' },
  { key: 'driftDayMean', label: 'Drift mean' },
  { key: 'driftByBlock', label: 'Drift by block' },
  { key: 'longestSittingMinutes', label: 'Longest sit' },
  { key: 'cameraBand', label: 'Camera' },
  { key: 'busyMinutes', label: 'Busy min' },
  { key: 'backToBackRuns', label: 'Back to back' },
  { key: 'longestBusyRunMinutes', label: 'Longest busy' },
  { key: 'meetingMinutesObserved', label: 'Meeting min' },
  { key: 'driftInMeetings', label: 'Drift in calls' },
  { key: 'driftOutsideMeetings', label: 'Drift outside' },
  { key: 'meetingSource', label: 'Meeting source' },
  { key: 'stressors', label: 'Stressors' },
  { key: 'dayStates', label: 'Told' },
  { key: 'feelTags', label: 'Feel tags' },
  { key: 'reminderLocalMinutes', label: 'Reminder' },
];

/** Minutes after local midnight as HH:MM; null is "not measured". */
export const clockOf = (minutes: unknown): string => {
  if (typeof minutes !== 'number' || !Number.isFinite(minutes)) return NOT_MEASURED;
  const m = Math.max(0, Math.round(minutes));
  return `${String(Math.floor(m / 60) % 24).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
};

/** A HabitDay cell: the told states' empty list is "not told", the clock columns read as a time, the rest as stored. */
export const habitDayCell = (key: string, v: unknown): string => {
  if (key === 'dayStates') return Array.isArray(v) && v.length ? v.join(', ') : 'not told';
  if (key === 'checkLocalMinutes' || key === 'reminderLocalMinutes') return v === null || v === undefined ? (key === 'reminderLocalMinutes' ? 'no reminder' : NOT_MEASURED) : clockOf(v);
  if (key === 'checkDone') return v === true ? 'done' : v === false ? 'not done' : NOT_MEASURED;
  if (key === 'driftByBlock' && v && typeof v === 'object' && !Array.isArray(v)) {
    return Object.entries(v as Record<string, { mean?: unknown }>).map(([block, b]) => `${block} ${cellValue(b?.mean ?? null)}`).join(', ') || NOT_MEASURED;
  }
  return cellValue(v);
};

export const ZONE_SOURCE_LABEL: Record<string, string> = {
  account: 'the account (User.timezone)',
  daily_check_result: 'their newest daily check (the account has none)',
  daily_check_session: 'their newest check session (the account has none)',
  active_mode_session: 'their newest Active Mode session (the account has none)',
  day_note: 'their newest evening note (the account has none)',
  evening_rundown: 'their newest evening rundown (the account has none)',
  reminder: 'their reminder (the account has none)',
  none: 'nowhere: no zone is recorded, so days are read in UTC',
};

export const zoneSourceLabel = (source: string): string => ZONE_SOURCE_LABEL[source] ?? source.replace(/_/g, ' ');

export const FELT_LABEL: Record<string, string> = { better: 'better', same: 'the same', worse: 'worse', unsure: 'not sure' };
export const feltLabel = (felt: string | null): string => (felt ? FELT_LABEL[felt] ?? felt : 'not answered');

/** "3 of 41 rows yesterday", or why there is no count. */
export const countLine = (t: Pick<MonitoringTable, 'built' | 'rowsYesterday' | 'total' | 'countNote' | 'expectedIn'>): string => {
  if (!t.built) return `not built yet${t.expectedIn ? ` (${t.expectedIn})` : ''}`;
  if (t.rowsYesterday === null || t.total === null) return t.countNote ?? NOT_MEASURED;
  return `${t.rowsYesterday.toLocaleString()} yesterday · ${t.total.toLocaleString()} in all`;
};

/** How many flags across the catalog are on right now, for the page head. */
export const flagTally = (signals: Pick<MonitoringSignal, 'flags'>[]): { on: number; off: number } => {
  const seen = new Map<string, boolean>();
  for (const s of signals) for (const f of s.flags) seen.set(f.name, f.live);
  let on = 0;
  for (const v of seen.values()) if (v) on += 1;
  return { on, off: seen.size - on };
};

/** A plan pick's blocked list as lines: "the_unwind (S2) until 2026-10-20". */
export const blockedLines = (blocked: unknown): string[] =>
  Array.isArray(blocked)
    ? blocked.map((b) => {
        if (!b || typeof b !== 'object') return String(b);
        const r = b as Record<string, unknown>;
        const id = typeof r.id === 'string' ? r.id : typeof r.activationId === 'string' ? r.activationId : 'an activation';
        const rule = typeof r.rule === 'string' ? ` (${r.rule})` : '';
        const until = typeof r.until === 'string' ? ` until ${r.until.slice(0, 10)}` : '';
        const why = typeof r.reason === 'string' ? `: ${r.reason}` : '';
        return `${id}${rule}${until}${why}`;
      })
    : [];

/** A plan pick's fired reasons as lines: "told_long_day, weight 3". */
export const reasonLines = (reasons: unknown): string[] =>
  Array.isArray(reasons)
    ? reasons.map((x) => {
        if (!x || typeof x !== 'object') return String(x);
        const r = x as Record<string, unknown>;
        const key = typeof r.key === 'string' ? r.key : typeof r.reasonKey === 'string' ? r.reasonKey : 'a reason';
        const on = typeof r.activationId === 'string' ? ` for ${r.activationId}` : typeof r.id === 'string' ? ` for ${r.id}` : '';
        const weight = typeof r.weight === 'number' ? `, weight ${r.weight}` : '';
        return `${key}${on}${weight}`;
      })
    : [];
